import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SideNavBar from '../components/SideNavBar';
import { createSale, getErrorMessage, getItems, getTypes } from '../services/authApi';
import { getSocketServerUrl } from '../services/socketConfig';

const SCANNER_CHANNEL_STORAGE_KEY = 'pos_scanner_channel';

const createScannerChannel = () => {
  const randomPart = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `POS-${randomPart}`;
};

const normalizeBarcode = (value) => String(value || '').trim();
const getLineTotal = (price, quantity, discountPercent) => {
  const lineSubtotal = Number(price || 0) * Number(quantity || 0);
  return lineSubtotal - (lineSubtotal * Number(discountPercent || 0)) / 100;
};

function PosPage({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [showItemOptions, setShowItemOptions] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [paymentTypeId, setPaymentTypeId] = useState('1');
  const [actualReceived, setActualReceived] = useState(0);
  const [customer, setCustomer] = useState('');
  const [address, setAddress] = useState('');
  const [lines, setLines] = useState([]);
  const [scannerChannel, setScannerChannel] = useState(() => {
    const saved = localStorage.getItem(SCANNER_CHANNEL_STORAGE_KEY);
    return saved || createScannerChannel();
  });
  const [copied, setCopied] = useState(false);
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const [lastScannedBarcode, setLastScannedBarcode] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [useExternalScanner, setUseExternalScanner] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const itemsRef = useRef([]);
  const addItemSoundRef = useRef(null);
  const scannerRef = useRef(null);
  const lastSentRef = useRef({ value: '', at: 0 });

  const socketUrl = useMemo(() => getSocketServerUrl(), []);
  const scannerUrl = useMemo(() => {
    const url = new URL('/scanner', window.location.origin);
    url.searchParams.set('channel', scannerChannel);
    return url.toString();
  }, [scannerChannel]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    addItemSoundRef.current = new Audio('/sounds/deep.mp3');
    return () => {
      if (addItemSoundRef.current) {
        addItemSoundRef.current.pause();
        addItemSoundRef.current.src = '';
        addItemSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [itemsData, typesData] = await Promise.all([getItems({ per_page: 100 }), getTypes()]);
        setItems(Array.isArray(itemsData) ? itemsData : itemsData?.data || []);
        setPaymentTypes(Array.isArray(typesData) ? typesData : typesData?.data || []);
        setError('');
      } catch (requestError) {
        setError(getErrorMessage(requestError, 'Failed to load POS data.'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    localStorage.setItem(SCANNER_CHANNEL_STORAGE_KEY, scannerChannel);
  }, [scannerChannel]);

  useEffect(() => {
    if (!useExternalScanner) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setSocketStatus('disconnected');
      return;
    }

    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      setSocketStatus('connecting');

      ws.onopen = () => {
        if (cancelled) return;
        setSocketStatus('connected');
        ws.send(JSON.stringify({
          type: 'join',
          role: 'pos',
          channel: scannerChannel,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type !== 'barcode_scanned') return;
          const barcode = normalizeBarcode(payload.barcode);
          if (barcode) handleBarcodeScanned(barcode);
        } catch {
          // Ignore malformed socket payloads.
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        setSocketStatus('disconnected');
        reconnectTimerRef.current = setTimeout(connect, 2000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [useExternalScanner, scannerChannel, socketUrl]);

  const stopCamera = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.stop().catch(() => {});
      scannerRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError('');
    
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      
      const scanner = new Html5Qrcode('scanner-region');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          const now = Date.now();
          const prev = lastSentRef.current;
          if (prev.value === decodedText && now - prev.at < 1500) {
            return;
          }
          lastSentRef.current = { value: decodedText, at: now };
          handleBarcodeScanned(decodedText);
        },
        () => {}
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setCameraError('Unable to start camera. Please allow camera permission.');
      stopCamera();
    }
  }, []);

  const toggleScanner = () => {
    if (scannerOpen) {
      stopCamera();
      setScannerOpen(false);
    } else {
      setScannerOpen(true);
      setUseExternalScanner(false);
      startCamera();
    }
  };

  const toggleExternalScanner = () => {
    setUseExternalScanner((prev) => !prev);
    if (!useExternalScanner) {
      stopCamera();
      setScannerOpen(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [stopCamera]);

  const addItemToCart = useCallback((item) => {
    if (!item) return;

    setLines((prev) => {
      const exists = prev.find((line) => line.item_id === item.id);
      if (exists) {
        return prev.map((line) =>
          line.item_id === item.id
            ? { ...line, quantity: Number(line.quantity || 0) + 1 }
            : line
        );
      }
      return [...prev, { item_id: item.id, quantity: 1, discount_percent: 0 }];
    });

    setSelectedItemId('');
    setItemSearch('');
    setShowItemOptions(false);
    setError('');
    setLastScannedBarcode('');
  }, []);

  const handleBarcodeScanned = useCallback(
    async (scannedBarcode) => {
      const normalized = normalizeBarcode(scannedBarcode);
      if (!normalized) return;

      setLastScannedBarcode(normalized);

      let matchedItem = itemsRef.current.find(
        (item) => normalizeBarcode(item.barcode) === normalized
      );

      if (!matchedItem) {
        try {
          const response = await getItems({ search: normalized, per_page: 100 });
          const list = Array.isArray(response) ? response : response?.data || [];
          matchedItem = list.find((item) => normalizeBarcode(item.barcode) === normalized);

          if (matchedItem) {
            setItems((prev) =>
              prev.some((current) => current.id === matchedItem.id) ? prev : [matchedItem, ...prev]
            );
          }
        } catch {
          setError('Failed to find item by barcode.');
          return;
        }
      }

      if (!matchedItem) {
        setError(`No item found for barcode: ${normalized}`);
        return;
      }

      addItemToCart(matchedItem);
      setSuccess(`Added ${matchedItem.name} from barcode ${normalized}`);
      if (addItemSoundRef.current) {
        addItemSoundRef.current.currentTime = 0;
        addItemSoundRef.current.play().catch(() => {});
      }
    },
    [addItemToCart]
  );

  const copyChannel = async () => {
    try {
      await navigator.clipboard.writeText(scannerChannel);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
    }
  };

  const selectedItem = useMemo(
    () => items.find((item) => String(item.id) === String(selectedItemId)),
    [items, selectedItemId]
  );
  const filteredItems = useMemo(() => {
    const keyword = itemSearch.trim().toLowerCase();
    if (!keyword) return items.slice(0, 20);
    return items.filter((item) => item.name?.toLowerCase().includes(keyword)).slice(0, 20);
  }, [items, itemSearch]);

  const subtotal = useMemo(
    () =>
      lines.reduce((sum, line) => {
        const currentItem = items.find((item) => item.id === line.item_id);
        const price = Number(currentItem?.price || 0);
        return sum + getLineTotal(price, line.quantity, line.discount_percent);
      }, 0),
    [lines, items]
  );

  const totalQuantity = useMemo(() => lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0), [lines]);

  const totalAfterDiscount = useMemo(
    () => subtotal - (subtotal * Number(discountPercentage || 0)) / 100,
    [subtotal, discountPercentage]
  );

  const grandTotal = useMemo(
    () => Math.round(totalAfterDiscount + (totalAfterDiscount * Number(taxPercentage || 0)) / 100),
    [totalAfterDiscount, taxPercentage]
  );

  const receivedAmount = useMemo(() => grandTotal, [grandTotal]);

  const receivableAmount = useMemo(
    () => Math.max(0, grandTotal - Number(actualReceived || 0)),
    [grandTotal, actualReceived]
  );

  const addLine = () => {
    if (!selectedItem) {
      setError('Please select an item.');
      return;
    }

    addItemToCart(selectedItem);
    if (addItemSoundRef.current) {
      addItemSoundRef.current.currentTime = 0;
      addItemSoundRef.current.play().catch(() => {});
    }
    setSuccess('');
  };

  const updateLineQuantity = (itemId, quantityValue) => {
    if (quantityValue === '') {
      setLines((prev) =>
        prev.map((line) => (line.item_id === itemId ? { ...line, quantity: '' } : line))
      );
      return;
    }

    const quantity = Number(quantityValue);
    if (Number.isNaN(quantity) || quantity < 0) return;

    setLines((prev) =>
      prev.map((line) => (line.item_id === itemId ? { ...line, quantity } : line))
    );
  };

  const updateLineDiscount = (itemId, discountValue) => {
    if (discountValue === '') {
      setLines((prev) =>
        prev.map((line) => (line.item_id === itemId ? { ...line, discount_percent: '' } : line))
      );
      return;
    }

    const discount = Number(discountValue);
    if (Number.isNaN(discount) || discount < 0 || discount > 100) return;

    setLines((prev) =>
      prev.map((line) => (line.item_id === itemId ? { ...line, discount_percent: discount } : line))
    );
  };

  const normalizeLineQuantity = (itemId) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.item_id !== itemId) return line;
        return { ...line, quantity: Math.max(1, Number(line.quantity || 1)) };
      })
    );
  };

  const normalizeLineDiscount = (itemId) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.item_id !== itemId) return line;
        const nextDiscount = Number(line.discount_percent || 0);
        return {
          ...line,
          discount_percent: Math.min(100, Math.max(0, nextDiscount)),
        };
      })
    );
  };

  const removeLine = (itemId) => {
    setLines((prev) => prev.filter((line) => line.item_id !== itemId));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!user?.id) {
      setError('User is required.');
      return;
    }

    if (lines.length === 0) {
      setError('Please add at least one item.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        user_id: user.id,
        customer: customer || null,
        address: address || null,
        discount_percentage: Number(discountPercentage || 0),
        tax_percentage: Number(taxPercentage || 0),
        payment_type_id: paymentTypeId ? Number(paymentTypeId) : null,
        received_amount: Number(receivedAmount || 0),
        actual_received: Number(actualReceived || 0),
        items: lines.map((line) => ({
          item_id: line.item_id,
          quantity: Number(line.quantity),
          discount_percent: Number(line.discount_percent || 0),
        })),
      };

      await createSale(payload);
      setLines([]);
      setDiscountPercentage(0);
      setTaxPercentage(0);
      setPaymentTypeId('1');
      setActualReceived(0);
      setCustomer('');
      setAddress('');
      setSelectedItemId('');
      setSuccess('Sale created successfully.');
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to create sale.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-maroon-50 to-maroon-100 md:flex-row">
      <SideNavBar user={user} onLogout={onLogout} />
      <div className="flex flex-1 p-4 md:p-6">
        <div className="w-full rounded-2xl bg-white p-4 md:p-6 shadow-xl shadow-maroon-900/10">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h1 className="text-3xl font-bold text-maroon-900">POS</h1>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={toggleScanner}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  scannerOpen
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'border border-maroon-700 text-maroon-800 hover:bg-maroon-700 hover:text-white'
                }`}
              >
                {scannerOpen ? 'Close Camera' : 'Scan Camera'}
              </button>
              <button
                type="button"
                onClick={toggleExternalScanner}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  useExternalScanner
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'border border-maroon-700 text-maroon-800 hover:bg-maroon-700 hover:text-white'
                }`}
              >
                {useExternalScanner ? 'External: ON' : 'External: OFF'}
              </button>
            </div>
          </div>

          {scannerOpen && (
            <div className="mb-4 rounded-xl border border-maroon-200 bg-black p-2">
              <div id="scanner-region" className="h-40 w-full overflow-hidden rounded-lg md:h-48" />
              {cameraError && <p className="mt-2 text-sm text-red-500">{cameraError}</p>}
              <div className="mt-2 flex justify-center">
                <div className="h-2 w-48 animate-pulse rounded-full bg-green-500" />
              </div>
            </div>
          )}

          {useExternalScanner && (
            <div className="mb-4 rounded-lg border border-maroon-100 bg-maroon-50 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-maroon-900">External Scanner Channel:</p>
                  <p className="text-lg font-bold text-maroon-800">{scannerChannel}</p>
                  <p className="text-xs text-maroon-600">Status: {socketStatus}</p>
                  {lastScannedBarcode && (
                    <p className="text-xs text-green-600">Last: {lastScannedBarcode}</p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={copyChannel}
                    className="rounded-lg bg-maroon-700 px-3 py-1 text-xs font-semibold text-white hover:bg-maroon-800"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setScannerChannel(createScannerChannel());
                    }}
                    className="rounded-lg border border-maroon-300 px-3 py-1 text-xs font-semibold text-maroon-700 hover:bg-maroon-100"
                  >
                    New Channel
                  </button>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-xs text-maroon-600">
                  Open scanner in new tab: <span className="font-mono text-xs">{scannerUrl}</span>
                </p>
              </div>
            </div>
          )}

          {loading ? <p className="text-maroon-700">Loading...</p> : null}

          {!loading ? (
            <>
              <div className="mb-4 flex flex-col gap-2 md:flex-row">
                <div className="relative w-full">
                  <input
                    type="text"
                    value={itemSearch}
                    placeholder="Search item..."
                    onFocus={() => setShowItemOptions(true)}
                    onBlur={() => {
                      setTimeout(() => setShowItemOptions(false), 120);
                    }}
                    onChange={(event) => {
                      setItemSearch(event.target.value);
                      setSelectedItemId('');
                    }}
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                  {showItemOptions ? (
                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-maroon-200 bg-white shadow-lg">
                      {filteredItems.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-maroon-700">No items found.</div>
                      ) : (
                        filteredItems.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              setSelectedItemId(String(item.id));
                              setItemSearch(`${item.name} - ${item.price}`);
                              setShowItemOptions(false);
                            }}
                            className="block w-full px-3 py-2 text-left text-sm text-maroon-900 hover:bg-maroon-50"
                          >
                            {item.name} - {item.price}
                          </button>
                        ))
                      )}
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={addLine}
                  className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800"
                >
                  Add Item
                </button>
              </div>

              <div className="mb-5 overflow-x-auto rounded-lg border border-maroon-100">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-maroon-50 text-maroon-900">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Item</th>
                      <th className="px-4 py-3 font-semibold">Price</th>
                      <th className="px-4 py-3 font-semibold">Quantity</th>
                      <th className="px-4 py-3 font-semibold">Discount %</th>
                      <th className="px-4 py-3 font-semibold">Total</th>
                      <th className="px-4 py-3 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length === 0 ? (
                      <tr>
                        <td className="px-4 py-3 text-maroon-700" colSpan={6}>
                          No items in cart.
                        </td>
                      </tr>
                    ) : (
                      lines.map((line) => {
                        const lineItem = items.find((item) => item.id === line.item_id);
                        const price = Number(lineItem?.price || 0);
                        const lineTotal = getLineTotal(price, line.quantity, line.discount_percent);

                        return (
                          <tr key={line.item_id} className="border-t border-maroon-100">
                            <td className="px-4 py-3 text-maroon-800">{lineItem?.name || '-'}</td>
                            <td className="px-4 py-3 text-maroon-800">{price}</td>
                            <td className="px-4 py-3 text-maroon-800">
                              <input
                                type="number"
                                min="1"
                                value={line.quantity}
                                onChange={(event) => updateLineQuantity(line.item_id, event.target.value)}
                                onBlur={() => normalizeLineQuantity(line.item_id)}
                                className="w-24 rounded-lg border border-maroon-200 px-3 py-1 outline-none ring-maroon-300 focus:ring-2"
                              />
                            </td>
                            <td className="px-4 py-3 text-maroon-800">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={line.discount_percent ?? 0}
                                onChange={(event) => updateLineDiscount(line.item_id, event.target.value)}
                                onBlur={() => normalizeLineDiscount(line.item_id)}
                                className="w-24 rounded-lg border border-maroon-200 px-3 py-1 outline-none ring-maroon-300 focus:ring-2"
                              />
                            </td>
                            <td className="px-4 py-3 text-maroon-800">{lineTotal}</td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                onClick={() => removeLine(line.item_id)}
                                className="rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Customer Name</label>
                  <input
                    type="text"
                    value={customer}
                    onChange={(event) => setCustomer(event.target.value)}
                    placeholder="Enter customer name"
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Address</label>
                  <input
                    type="text"
                    value={address}
                    onChange={(event) => setAddress(event.target.value)}
                    placeholder="Enter address"
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Sale Discount %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={discountPercentage}
                    onChange={(event) => setDiscountPercentage(event.target.value)}
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Tax %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={taxPercentage}
                    onChange={(event) => setTaxPercentage(event.target.value)}
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Payment Type</label>
                  <select
                    value={paymentTypeId}
                    onChange={(event) => setPaymentTypeId(event.target.value)}
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  >
                    <option value="">Select payment type</option>
                    {paymentTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-maroon-900">Received Amount</label>
                  <input
                    type="number"
                    min="0"
                    value={actualReceived}
                    onChange={(event) => setActualReceived(event.target.value)}
                    placeholder="Enter amount received"
                    className="w-full rounded-lg border border-maroon-200 px-3 py-2 outline-none ring-maroon-300 focus:ring-2"
                  />
                </div>

                <div className="rounded-lg border border-maroon-100 bg-maroon-50 p-4 md:col-span-2">
                  <p className="text-sm text-maroon-900">Quantity: {totalQuantity}</p>
                  <p className="text-sm text-maroon-900">Subtotal: {subtotal}</p>
                  <p className="text-sm text-maroon-900">
                    Sale Discount Amount: {Math.round((subtotal * Number(discountPercentage || 0)) / 100)}
                  </p>
                  <p className="text-sm text-maroon-900">
                    Tax Amount: {Math.round((totalAfterDiscount * Number(taxPercentage || 0)) / 100)}
                  </p>
                  <p className="text-sm text-maroon-900">Total: {receivedAmount}</p>
                  <p className="text-sm font-semibold text-maroon-900">Actual Received: {actualReceived || 0}</p>
                  <p className="text-sm font-semibold text-red-600">Receivable: {receivableAmount}</p>
                </div>

                {error ? <p className="text-sm font-medium text-red-600 md:col-span-2">{error}</p> : null}
                {success ? <p className="text-sm font-medium text-green-700 md:col-span-2">{success}</p> : null}

                <div className="md:col-span-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg bg-maroon-700 px-4 py-2 text-sm font-semibold text-white hover:bg-maroon-800 disabled:opacity-60"
                  >
                    {submitting ? 'Saving Sale...' : 'Create Sale'}
                  </button>
                </div>
              </form>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PosPage;
